import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from './layout/Layout';
import { notificationApi, sessionApi } from '../services/api';
import wsService from '../services/websocket';
import './Notifications.css';

const SECURITY_TYPES = new Set([
  'security',
  'security_event',
  'trusted_device.added',
  'trusted_device.revoked',
  'session.epoch_bumped',
  '2sv.method_enrolled',
  '2sv.method_removed',
  '2sv.backup_code_used',
  '2sv.step_up_failed',
  'org.security_policy_changed',
]);

const isSecurityEvent = (event) => {
  const type = event.type || event.event_type || event.push_category || event.category;
  return SECURITY_TYPES.has(type) || String(type || '').startsWith('SEC-EVT') || String(event.url || '').includes('/security/events');
};

const normalizeEvent = (event) => {
  const metadata = event.metadata && typeof event.metadata === 'object' ? event.metadata : {};
  return {
    ...event,
    id: event.id || event.event_id || metadata.event_id,
    title: event.title || metadata.title || 'Security event',
    text: event.text || event.body || event.message || metadata.message || '',
    type: event.type || event.event_type || event.push_category || event.category || metadata.event_type || 'security',
    severity: event.severity || metadata.severity || 'info',
    created_at: event.created_at || event.occurred_at || metadata.occurred_at || new Date().toISOString(),
    session_id: event.session_id || metadata.session_id || event.triggered_by_session_id || metadata.triggered_by_session_id,
    device_label: event.device_label || metadata.device_label,
    ip: event.ip || metadata.ip,
    approx_city: event.approx_city || metadata.approx_city,
  };
};

const SecurityEvents = ({ embedded = false, eventId }) => {
  const { id: routeId } = useParams();
  const id = eventId || routeId;
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [revoking, setRevoking] = useState(false);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await notificationApi.getNotifications({ limit: 100 });
      const rows = response.data?.data || [];
      setEvents(rows.filter(isSecurityEvent).map(normalizeEvent));
    } catch (err) {
      console.error('Failed to load security events:', err);
      setError('Failed to load security events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    wsService.connect();
    const unsubscribe = wsService.subscribe('security:event', (event) => {
      setEvents((prev) => [normalizeEvent(event), ...prev]);
    });
    return () => unsubscribe();
  }, []);

  const selectedEvent = useMemo(() => {
    if (!id) {
      return events[0] || null;
    }
    return events.find((event) => String(event.id) === String(id)) || null;
  }, [events, id]);

  const revokeSession = async () => {
    if (!selectedEvent?.session_id) {
      setActionMessage('This event does not include a revokable session.');
      return;
    }

    try {
      setRevoking(true);
      setActionMessage('');
      await sessionApi.revokeSession(selectedEvent.session_id);
      setActionMessage('Session revoke requested.');
    } catch (err) {
      console.error('Failed to revoke session:', err);
      setActionMessage(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to revoke this session.');
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (value) => new Date(value).toLocaleString();

  const content = (
      <div className="notifications-page security-events-page">
        <header className="page-header">
          <div>
            <h1>Security Events</h1>
            <p className="subtitle">Review sign-in, device, and account-protection activity.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={loadEvents} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </header>

        {error && <div className="settings-message settings-error">{error}</div>}
        {actionMessage && <div className="settings-message settings-info">{actionMessage}</div>}

        {loading ? (
          <div className="loading">Loading security events...</div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔒</span>
            <p>No security events found</p>
          </div>
        ) : (
          <div className="security-events-layout">
            <div className="security-events-list">
              {events.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  className={`security-event-row ${String(selectedEvent?.id) === String(event.id) ? 'active' : ''}`}
                  onClick={() => navigate(embedded ? `/app/profile?tab=security&id=${event.id}` : `/app/account/security/events/${event.id}`)}
                >
                  <span className={`severity-dot severity-${event.severity}`} />
                  <span>
                    <strong>{event.title}</strong>
                    <small>{formatDate(event.created_at)}</small>
                  </span>
                </button>
              ))}
            </div>

            {selectedEvent && (
              <article className="security-event-detail">
                <div className="security-event-detail-header">
                  <div>
                    <h2>{selectedEvent.title}</h2>
                    <p>{formatDate(selectedEvent.created_at)}</p>
                  </div>
                  <span className={`security-severity security-severity-${selectedEvent.severity}`}>
                    {selectedEvent.severity}
                  </span>
                </div>
                <p>{selectedEvent.text}</p>
                <dl>
                  <div>
                    <dt>Event type</dt>
                    <dd>{selectedEvent.type}</dd>
                  </div>
                  <div>
                    <dt>Device</dt>
                    <dd>{selectedEvent.device_label || 'Unknown device'}</dd>
                  </div>
                  <div>
                    <dt>Location</dt>
                    <dd>{selectedEvent.approx_city || selectedEvent.ip || 'Unknown location'}</dd>
                  </div>
                </dl>
                {selectedEvent.session_id && (
                  <button type="button" className="btn btn-danger" onClick={revokeSession} disabled={revoking}>
                    {revoking ? 'Revoking...' : 'Revoke session'}
                  </button>
                )}
              </article>
            )}
          </div>
        )}
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default SecurityEvents;
